import { useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { z } from 'zod';
import { client } from '@/api';
import { Link } from '@tanstack/react-router';

interface EmailInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  label?: string;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  required?: boolean;
  returnTo?: string;
  onAccountExists?: (exists: boolean, provider?: string | null) => void;
}

async function checkEmailUnique(
  email: string,
): Promise<{ valid: boolean; reason?: string; provider?: string | null }> {
  try {
    const response = await client.instance.get('/public/check-email', {
      params: { email },
    });
    const data = response.data as { exists: boolean; provider?: string | null };
    if (data.exists) {
      return {
        valid: false,
        reason: 'account-exists',
        provider: data.provider,
      };
    }
    return { valid: true };
  } catch {
    return { valid: true };
  }
}

const emailSchema = z.string().email('Please enter a valid email address');

export function EmailInput({
  value,
  onChange,
  onBlur,
  label = 'Email',
  disabled,
  readOnly,
  error,
  required,
  returnTo,
  onAccountExists,
}: EmailInputProps) {
  const [touched, setTouched] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [asyncResult, setAsyncResult] = useState<{
    value: string;
    status: 'valid' | 'exists' | 'error';
    message?: string;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const currentResult = asyncResult?.value === value ? asyncResult : null;
  const isValidated = currentResult?.status === 'valid';
  const accountExists = currentResult?.status === 'exists';
  const asyncError =
    currentResult?.status === 'error' ? currentResult.message : null;

  let localError: string | null = null;
  if (touched) {
    if (!value) {
      localError = required ? 'Email is required' : null;
    } else {
      const result = emailSchema.safeParse(value);
      if (!result.success) {
        localError = result.error.issues?.[0]?.message ?? 'Invalid email';
      }
    }
  }

  const handleBlur = async () => {
    setTouched(true);
    if (onBlur) onBlur();
    if (!value || localError || isValidated) return;
    setIsValidating(true);
    setAsyncResult(null);
    if (abortRef.current) abortRef.current.abort();
    const abortController = new AbortController();
    abortRef.current = abortController;
    try {
      const res = await checkEmailUnique(value);
      if (abortController.signal.aborted) return;
      if (!res.valid) {
        if (res.reason === 'account-exists') {
          setAsyncResult({ value, status: 'exists' });
          onAccountExists?.(true, res.provider);
        } else {
          setAsyncResult({
            value,
            status: 'error',
            message: res.reason || 'Email is not valid',
          });
        }
      } else {
        setAsyncResult({ value, status: 'valid' });
        onAccountExists?.(false);
      }
    } catch {
      if (!abortController.signal.aborted) {
        setAsyncResult({
          value,
          status: 'error',
          message: 'Could not validate email',
        });
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsValidating(false);
      }
    }
  };

  let icon: React.ReactNode = null;
  if (isValidating) {
    icon = <Loader2 className="animate-spin text-blue-500" size={16} />;
  } else if ((localError || asyncError || accountExists) && value) {
    icon = <AlertCircle className="text-red-500" size={16} />;
  } else if (isValidated && value) {
    icon = <CheckCircle className="text-green-500" size={16} />;
  }

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      <FormControl>
        <div className="relative">
          <Input
            type="email"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={readOnly}
            placeholder="Email address"
            aria-invalid={!!(error || localError || asyncError || accountExists)}
            required={required}
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2">
            {icon}
          </span>
        </div>
      </FormControl>
      {accountExists ? (
        <p className="text-sm text-amber-600">
          You already have an account.{' '}
          <Link
            to="/login"
            search={returnTo ? { returnTo } : undefined}
            className="underline font-medium hover:text-amber-700"
          >
            Sign in
          </Link>{' '}
          instead.
        </p>
      ) : (
        <FormMessage>{error || localError || asyncError}</FormMessage>
      )}
    </FormItem>
  );
}
